import Loader from '@components/shared/loader/Loader';
import { RoutePaths } from '@enums/RoutePaths';
import { CampaignTest } from '@pages/admin/CampaignTest';
import { FC, Suspense } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router';
import * as Pages from './pages';
import { ProtectedRoute } from './ProtectedRoutes';
import { AppDispatch } from './store/AppState';
import { setUser } from './store/identity/AuthSlice';

const AppRoutes: FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  dispatch(setUser());

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path='/' element={<Navigate to={RoutePaths.Home} state={{ name: 'default' }} />} />

        {/* Route legacy login route to Home */}
        <Route
          path='/login'
          element={<Navigate to={RoutePaths.Home} state={{ name: 'default' }} />}
        />
        <Route path={RoutePaths.LoginCallback} element={<Pages.Login />} />
        <Route path={RoutePaths.Logout} element={<Pages.Logout />} />
        <Route path='*' element={<Pages.PageNotFound />} />
        <Route path={RoutePaths.AuthError} element={<Pages.AuthError />} />
        {/* Unauthorized */}
        <Route path={RoutePaths.Unauthorized} element={<Pages.Unauthorized />} />

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          {/* General */}
          <Route path={RoutePaths.RobotException} element={<Pages.RobotException />} />
          <Route
            path={'/noDealSheetResult'}
            element={<Pages.NoDealsheetResult message={'no dealsheet results'} />}
          />
          <Route path={RoutePaths.DealsheetError} element={<Pages.DealsheetError />} />

          {/* Off IP */}
          <Route path={RoutePaths.OffIp} element={<Pages.OffIp />} />

          {/* Home */}
          <Route path={RoutePaths.Home} element={<Pages.Home openedFromModal={false} />} />

          {/* Incentive Advantage */}
          <Route path={RoutePaths.EssentialDashboard} element={<Pages.EUIDashboard />} />
          <Route path={RoutePaths.EssentialDealsheet} element={<Pages.EUIDealSheet />} />
          <Route path={RoutePaths.EssentialDealHistory} element={<Pages.EUIDealHistory />} />

          {/* Mastermind */}
          <Route path={RoutePaths.Portfolio} element={<Pages.Portfolio />} />
          <Route path={RoutePaths.DealSheet} element={<Pages.DealSheet />} />
          <Route path={RoutePaths.DealSheetVin} element={<Pages.DealSheetVin />} />
          {/* pwa */}
          <Route path={RoutePaths.PWA} element={<Pages.PWA />} />
          <Route path={RoutePaths.PWARequestAccess} element={<Pages.PWA />} />
          <Route path={RoutePaths.PWAError} element={<Pages.PwaError />} />
          <Route path={RoutePaths.PWADesktopError} element={<Pages.DesktopModeError />} />

          {/* Invitation */}
          <Route path={RoutePaths.Invitation} element={<Pages.Invitation />} />
          <Route path={RoutePaths.GetMobileApp} element={<Pages.GetMobileApp />} />

          {/* Favorite Deals */}
          <Route path={RoutePaths.Favorites} element={<Pages.Favorites />} />

          {/* Recent Deals */}
          <Route path={RoutePaths.Recent} element={<Pages.Recent />} />

          {/* Your Profile */}
          <Route path={RoutePaths.YourProfile} element={<Pages.Profile />} />

          {/* Support */}
          <Route path={RoutePaths.Support} element={<Pages.Support />} />

          {/* Mobile App */}
          <Route path={RoutePaths.InvitationMobile} element={<Pages.GetMobileApp />} />

          {/* Reporting */}
          <Route
            path={RoutePaths.PrivateIncentiveInSights}
            element={<Pages.PrivateIncentiveInSights />}
          />
          <Route path={RoutePaths.PerformanceInsights} element={<Pages.PerformanceInsights />} />
          <Route path={RoutePaths.ServicePortfolio} element={<Pages.ServicePortfolio />} />

          {/* Private Incentive Lookup */}
          <Route
            path={RoutePaths.PrivateIncentiveLookup}
            element={<Pages.PrivateIncentiveLookup />}
          />

          {/* Acquire */}
          <Route path={RoutePaths.Acquire} element={<Pages.Acquire />} />
          <Route path={RoutePaths.AcquireProspectProfile} element={<Pages.Acquire />} />

          {/* Market Impact */}
          <Route path={RoutePaths.MarketImpact} element={<Pages.MarketImpact />} />
          <Route path={RoutePaths.MarketImpactSummary} element={<Pages.MarketImpactSummary />} />

          {/* Turn */}
          <Route path={RoutePaths.Turn} element={<Pages.TurnPortfolio />} />
          <Route path={RoutePaths.TurnDetails} element={<Pages.TurnDetails />} />

          {/* other Routes */}
          <Route path={RoutePaths.BulkPrint} element={<Pages.BulkPrint />} />

          {/* Service Portfolio */}
          <Route path={RoutePaths.Service} element={<Pages.Service />} />

          {/* Store Settings */}
          <Route path={RoutePaths.Admin} element={<Pages.Admin />}>
            <Route path={RoutePaths.AdminDashboard} element={<Pages.AdminDashboard />} />
            <Route path={RoutePaths.OfferManager} element={<Pages.OfferManager />} />
            <Route path={RoutePaths.Tradebooks} element={<Pages.Tradebooks />} />
            <Route path={RoutePaths.PreownedRates} element={<Pages.PreownedRates />} />
            <Route path={RoutePaths.CRMMapper} element={<Pages.CRMMapper />} />
            <Route path={RoutePaths.DMSMapper} element={<Pages.DMSMapper />} />
            <Route path={RoutePaths.UserManagement} element={<Pages.UserManagement />} />
            <Route path={RoutePaths.HoursOfOperation} element={<Pages.HoursOfOperation />} />
            <Route path={RoutePaths.DataExport} element={<Pages.DataExport />} />
          </Route>

          {/* Digital Audience */}
          <Route path={RoutePaths.DigitalAudience + '/*'} element={<Pages.DigitalAudience />} />

          <Route path={RoutePaths.CampaignTest} element={<CampaignTest />}></Route>

          {/* Carfax call-back */}
          <Route path={RoutePaths.CarfaxCallback} element={<Pages.CarfaxCallBack />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
